import { ChangeEvent, FormEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { Alert, Button, IconButton, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useI18n } from '../../../shared/i18n';
import { useAuthStore, authSelectors } from '../model/selectors';
import { AuthFieldErrors } from '../model/types';
import { CodeStep } from './CodeStep';
import { PhoneStep } from './PhoneStep';

export type AuthMode = 'login' | 'register' | 'recovery';

export interface AuthOutcomeContext {
  phone: string;
}

interface AuthFeatureProps {
  onCancel: (context?: AuthOutcomeContext) => void;
  onAuthenticated?: (context?: AuthOutcomeContext) => Promise<void> | void;
  subtitle?: string;
  allowedModes?: AuthMode[];
  initialMode?: AuthMode;
  initialPhone?: string;
}

interface AuthFeatureContentProps {
  onCancel: (context?: AuthOutcomeContext) => void;
  onAuthenticated?: (context?: AuthOutcomeContext) => Promise<void> | void;
  subtitle?: string;
  resolvedModes: AuthMode[];
  initialMode: AuthMode;
  initialPhone?: string;
}

type PasswordOperationStep = 'request' | 'code' | 'password';

const ModeLink = styled(Link)({
  alignSelf: 'flex-start',
}) as typeof Link;

export function AuthFeature({
  onCancel,
  onAuthenticated,
  subtitle,
  allowedModes = ['login'],
  initialMode,
  initialPhone,
}: AuthFeatureProps) {
  const resolvedModes = useMemo<AuthMode[]>(() => (allowedModes.length > 0 ? [...allowedModes] : ['login']), [allowedModes]);
  const initialResolvedMode = initialMode && resolvedModes.includes(initialMode) ? initialMode : resolvedModes[0];
  const resetKey = `${resolvedModes.join(':')}|${initialResolvedMode}|${initialPhone ?? ''}`;

  return (
    <AuthFeatureContent
      key={resetKey}
      onCancel={onCancel}
      onAuthenticated={onAuthenticated}
      subtitle={subtitle}
      resolvedModes={resolvedModes}
      initialMode={initialResolvedMode}
      initialPhone={initialPhone}
    />
  );
}

const secondaryModesMap: Record<AuthMode, AuthMode[]> = {
  login: ['register'],
  register: ['login'],
  recovery: ['login', 'register'],
};

function AuthFeatureContent({ onCancel, onAuthenticated, subtitle, resolvedModes, initialMode, initialPhone }: AuthFeatureContentProps) {
  const { t } = useI18n();

  const requestPasswordOperationCode = useAuthStore(authSelectors.requestPasswordOperationCode);
  const loginWithPassword = useAuthStore(authSelectors.loginWithPassword);
  const registerWithPassword = useAuthStore(authSelectors.registerWithPassword);
  const recoverPassword = useAuthStore(authSelectors.recoverPassword);
  const loading = useAuthStore(authSelectors.authLoading);

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [phone, setPhone] = useState(initialPhone ?? '');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [passwordOperationStep, setPasswordOperationStep] = useState<PasswordOperationStep>('request');
  const [retryAfter, setRetryAfter] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<AuthFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const activeMode = resolvedModes.includes(mode) ? mode : resolvedModes[0];
  const isPasswordOperationMode = activeMode === 'register' || activeMode === 'recovery';
  const isCodeStepVisible = isPasswordOperationMode && passwordOperationStep === 'code';
  const isPasswordStepVisible = isPasswordOperationMode && passwordOperationStep === 'password';

  const resetTransientState = () => {
    setPasswordOperationStep('request');
    setRetryAfter(0);
    setCode('');
    setPassword('');
    setNewPassword('');
    setFormError(null);
    setFieldErrors({});
  };

  const modeTitle = useMemo(() => {
    if (activeMode === 'register') {
      return t('main.authDialog.register');
    }

    if (activeMode === 'recovery') {
      return t('main.authDialog.restorePassword');
    }

    return t('main.authDialog.login');
  }, [activeMode, t]);

  useEffect(() => {
    if (retryAfter <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setRetryAfter((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [retryAfter]);

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPhone(event.target.value);
    setFieldErrors((current) => ({ ...current, phone: undefined }));
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setFieldErrors((current) => ({ ...current, password: undefined }));
  };

  const handleNewPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
    setFieldErrors((current) => ({ ...current, newPassword: undefined }));
  };

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value.slice(0, 6));
    setFieldErrors((current) => ({ ...current, code: undefined }));
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const switchMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    resetTransientState();
  };

  const handleRequestCode = async () => {
    setFormError(null);
    setFieldErrors({});

    if (activeMode !== 'register' && activeMode !== 'recovery') {
      return;
    }

    const operation = activeMode === 'register' ? 'register' : 'recovery';
    const result = await requestPasswordOperationCode(phone, operation);
    if (!result.success) {
      setFieldErrors(result.fieldErrors ?? {});
      setFormError(result.formError ?? null);
      return;
    }

    setPasswordOperationStep('code');
    setRetryAfter(60);
    setCode('');
  };

  const handleConfirmCode = () => {
    if (code.length !== 6) {
      return;
    }

    setPasswordOperationStep('password');
  };

  const handleVerify = async () => {
    setFormError(null);
    setFieldErrors({});

    if (activeMode === 'login') {
      const result = await loginWithPassword(phone, password);
      if (!result.success) {
        setFieldErrors(result.fieldErrors ?? {});
        setFormError(result.formError ?? null);
        return;
      }

      await onAuthenticated?.({ phone });
      return;
    }

    if (activeMode === 'register') {
      const result = await registerWithPassword(phone, code, password);
      if (!result.success) {
        setFieldErrors(result.fieldErrors ?? {});
        setFormError(result.formError ?? null);
        return;
      }

      await onAuthenticated?.({ phone });
      return;
    }

    const result = await recoverPassword(phone, code, newPassword);
    if (!result.success) {
      setFieldErrors(result.fieldErrors ?? {});
      setFormError(result.formError ?? null);
      return;
    }

    await onAuthenticated?.({ phone });
  };

  const canRequestCode = !loading && !!phone.trim() && retryAfter === 0 && isPasswordOperationMode;
  const canConfirmCode = !loading && code.length === 6 && isCodeStepVisible;
  const canSubmitFinal =
    !loading &&
    !!phone.trim() &&
    ((activeMode === 'login' && !!password.trim()) ||
      (activeMode === 'register' && isPasswordStepVisible && code.length === 6 && !!password.trim()) ||
      (activeMode === 'recovery' && isPasswordStepVisible && code.length === 6 && !!newPassword.trim()));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (activeMode === 'login') {
      if (!canSubmitFinal) {
        return;
      }

      void handleVerify();
      return;
    }

    if (passwordOperationStep === 'request') {
      if (!canRequestCode) {
        return;
      }

      void handleRequestCode();
      return;
    }

    if (passwordOperationStep === 'code') {
      if (!canConfirmCode) {
        return;
      }

      handleConfirmCode();
      return;
    }

    if (!canSubmitFinal) {
      return;
    }

    void handleVerify();
  };

  const secondaryModes = secondaryModesMap[activeMode] ?? [];

  const renderModeLink = (targetMode: AuthMode) => {
    const labels: Record<AuthMode, string> = {
      login: t('main.authDialog.login'),
      register: t('main.authDialog.register'),
      recovery: t('main.authDialog.restorePassword'),
    };

    return (
      <ModeLink
        key={targetMode}
        component="button"
        type="button"
        underline="hover"
        color="primary"
        onClick={() => switchMode(targetMode)}
        disabled={loading}
      >
        {labels[targetMode]}
      </ModeLink>
    );
  };

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={2.5}>
      <div>
        <Typography variant="h5" gutterBottom>
          {modeTitle}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </div>

      {formError ? <Alert severity="error">{formError}</Alert> : null}

      <PhoneStep
        value={phone}
        onChange={handlePhoneChange}
        label={t('main.authDialog.phoneField')}
        error={fieldErrors.phone}
        disabled={loading}
      />

      {activeMode === 'login' ? (
        <TextField
          value={password}
          onChange={handlePasswordChange}
          label={t('main.authDialog.passwordField')}
          type={showPassword ? 'text' : 'password'}
          error={Boolean(fieldErrors.password)}
          helperText={fieldErrors.password ?? ' '}
          disabled={loading}
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ) : null}

      {isCodeStepVisible ? (
        <CodeStep
          value={code}
          onChange={handleCodeChange}
          label={t('main.authDialog.codeField')}
          error={fieldErrors.code}
          disabled={loading}
        />
      ) : null}

      {activeMode === 'register' && isPasswordStepVisible ? (
        <TextField
          value={password}
          onChange={handlePasswordChange}
          label={t('main.authDialog.passwordField')}
          type={showPassword ? 'text' : 'password'}
          error={Boolean(fieldErrors.password)}
          helperText={fieldErrors.password ?? ' '}
          disabled={loading}
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ) : null}

      {activeMode === 'recovery' && isPasswordStepVisible ? (
        <TextField
          value={newPassword}
          onChange={handleNewPasswordChange}
          label={t('main.authDialog.passwordField')}
          type={showNewPassword ? 'text' : 'password'}
          error={Boolean(fieldErrors.newPassword)}
          helperText={fieldErrors.newPassword ?? ' '}
          disabled={loading}
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleToggleNewPasswordVisibility}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    disabled={loading}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {isPasswordOperationMode && passwordOperationStep === 'request' ? (
          <Button variant="outlined" type="button" onClick={() => void handleRequestCode()} disabled={!canRequestCode} loading={loading}>
            {t('main.authDialog.requestCode')}
          </Button>
        ) : null}

        {isCodeStepVisible ? (
          <>
            <Button
              variant="outlined"
              type="button"
              onClick={() => void handleRequestCode()}
              disabled={loading || retryAfter > 0}
              loading={loading}
            >
              {retryAfter > 0
                ? t('main.authDialog.resendIn', {
                    seconds: retryAfter,
                  })
                : t('main.authDialog.resend')}
            </Button>

            <Button variant="contained" type="submit" disabled={!canConfirmCode} loading={loading}>
              {t('main.authDialog.confirmCode')}
            </Button>
          </>
        ) : null}

        {activeMode === 'login' || isPasswordStepVisible ? (
          <Button variant="contained" type="submit" disabled={!canSubmitFinal} loading={loading}>
            {activeMode === 'login'
              ? t('main.authDialog.login')
              : activeMode === 'recovery'
                ? t('main.authDialog.changePassword')
                : t('main.authDialog.register')}
          </Button>
        ) : null}

        <Button type="button" variant="text" onClick={() => onCancel({ phone })} disabled={loading}>
          {t('main.authDialog.cancel')}
        </Button>
      </Stack>

      {activeMode === 'login' && (resolvedModes.includes('recovery') || resolvedModes.includes('register')) ? (
        <Stack spacing={0.75}>
          {resolvedModes.includes('recovery') ? (
            <ModeLink
              component="button"
              type="button"
              underline="hover"
              color="primary"
              onClick={() => switchMode('recovery')}
              disabled={loading}
            >
              {t('main.authDialog.restorePassword')}
            </ModeLink>
          ) : null}
          {resolvedModes.includes('register') ? (
            <ModeLink
              component="button"
              type="button"
              underline="hover"
              color="primary"
              onClick={() => switchMode('register')}
              disabled={loading}
            >
              {t('main.authDialog.register')}
            </ModeLink>
          ) : null}
        </Stack>
      ) : null}

      {secondaryModes.length > 0 &&
      !(activeMode === 'login' && (resolvedModes.includes('recovery') || resolvedModes.includes('register'))) ? (
        <Stack spacing={0.75}>{secondaryModes.map(renderModeLink)}</Stack>
      ) : null}
    </Stack>
  );
}
